import { Injectable } from "@angular/core";
import { BehaviorSubject, Observable, Subject } from "rxjs";

export interface ParentCheckboxState {
  checked: boolean;
}

/**
 * CheckboxStateService
 * --------------------------------------------------------------------
 * Owns the parent/child checkbox enable/include cross-component streams
 * that previously lived on `ApiService` (DimmingInterface, Dali102,
 * Dali102and207, Aux, Lsi, AocMultiChannel). Names match the legacy
 * `ApiService` API so consumers do not need source changes.
 */
@Injectable({ providedIn: "root" })
export class CheckboxStateService {
  // --- Per-feature "panel enabled" flags ----------------------------
  readonly dimmingInterfaceEnableCheckbox = new BehaviorSubject<boolean>(false);
  readonly getDimmingInterfaceEnableCheckbox: Observable<boolean> =
    this.dimmingInterfaceEnableCheckbox.asObservable();

  readonly Dali102EnableCheckbox = new BehaviorSubject<boolean>(false);
  readonly getDali102EnableCheckbox: Observable<boolean> =
    this.Dali102EnableCheckbox.asObservable();

  readonly Dali102and207EnableCheckbox = new BehaviorSubject<boolean>(false);
  readonly getDali102and207EnableCheckbox: Observable<boolean> =
    this.Dali102and207EnableCheckbox.asObservable();

  readonly AuxEnableCheckbox = new BehaviorSubject<boolean>(false);
  readonly getAuxEnableCheckbox: Observable<boolean> = this.AuxEnableCheckbox.asObservable();

  readonly LsiEnableCheckbox = new BehaviorSubject<boolean>(false);
  readonly getLsiEnableCheckbox: Observable<boolean> = this.LsiEnableCheckbox.asObservable();

  readonly AocMultiChannelEnableCheckbox = new BehaviorSubject<boolean>(false);
  readonly getAocMultiChannelEnableCheckbox: Observable<boolean> =
    this.AocMultiChannelEnableCheckbox.asObservable();

  readonly OtpEnableCheckbox = new BehaviorSubject<boolean>(false);
  readonly getOtpEnableCheckbox: Observable<boolean> = this.OtpEnableCheckbox.asObservable();

  readonly DscEnableCheckbox = new BehaviorSubject<boolean>(false);
  readonly getDscEnableCheckbox: Observable<boolean> = this.DscEnableCheckbox.asObservable();

  readonly LowMainsLockEnableCheckbox = new BehaviorSubject<boolean>(false);
  readonly getLowMainsLockEnableCheckbox: Observable<boolean> = this.LowMainsLockEnableCheckbox.asObservable();

  readonly DipswitchEnableCheckbox = new BehaviorSubject<boolean>(false);
  readonly getDipswitchEnableCheckbox: Observable<boolean> = this.DipswitchEnableCheckbox.asObservable();

  // --- Parent->child notification streams (latest checked state) ----
  readonly dali102AddressingParentCheckbox = new BehaviorSubject<ParentCheckboxState | null>(null);
  readonly getDali102AddressingParentCheckbox: Observable<ParentCheckboxState | null> =
    this.dali102AddressingParentCheckbox.asObservable();

  readonly dali102and207ConfigurationParentCheckbox =
    new BehaviorSubject<ParentCheckboxState | null>(null);
  readonly getDali102and207ConfigurationParentCheckbox: Observable<ParentCheckboxState | null> =
    this.dali102and207ConfigurationParentCheckbox.asObservable();

  readonly lsiParentCheckbox = new BehaviorSubject<ParentCheckboxState | null>(null);
  readonly getLsiParentCheckbox: Observable<ParentCheckboxState | null> =
    this.lsiParentCheckbox.asObservable();

  readonly aocMultiChannelParentCheckbox = new BehaviorSubject<ParentCheckboxState | null>(null);
  readonly getAocMultiChannelParentCheckbox: Observable<ParentCheckboxState | null> =
    this.aocMultiChannelParentCheckbox.asObservable();

  readonly otpParentCheckbox = new BehaviorSubject<ParentCheckboxState | null>(null);
  readonly getOtpParentCheckbox: Observable<ParentCheckboxState | null> =
    this.otpParentCheckbox.asObservable();

  readonly dscParentCheckbox = new BehaviorSubject<ParentCheckboxState | null>(null);
  readonly getDscParentCheckbox: Observable<ParentCheckboxState | null> =
    this.dscParentCheckbox.asObservable();

  readonly lowmainslockParentCheckbox = new BehaviorSubject<ParentCheckboxState | null>(null);
  readonly getLowMainsLockParentCheckbox: Observable<ParentCheckboxState | null> =
    this.lowmainslockParentCheckbox.asObservable();

  readonly dipswitchParentCheckbox = new BehaviorSubject<ParentCheckboxState | null>(null);
  readonly getDipswitchParentCheckbox: Observable<ParentCheckboxState | null> =
    this.dipswitchParentCheckbox.asObservable();

  // --- Imperative one-shot include requests -------------------------
  readonly dali102AddressingIncludeRequest = new Subject<boolean>();
  readonly getDali102AddressingIncludeRequest: Observable<boolean> =
    this.dali102AddressingIncludeRequest.asObservable();

  readonly dali102and207ConfigurationIncludeRequest = new Subject<boolean>();
  readonly getDali102and207ConfigurationIncludeRequest: Observable<boolean> =
    this.dali102and207ConfigurationIncludeRequest.asObservable();

  // --- Enable / disable convenience setters -------------------------
  enableDimmingInterfaceCheckbox(): void {
    this.dimmingInterfaceEnableCheckbox.next(true);
  }

  enableDali102Checkbox(): void {
    this.Dali102EnableCheckbox.next(true);
  }
  disableDali102Checkbox(): void {
    this.Dali102EnableCheckbox.next(false);
  }

  enableDali102and207Checkbox(): void {
    this.Dali102and207EnableCheckbox.next(true);
  }
  disableDali102and207Checkbox(): void {
    this.Dali102and207EnableCheckbox.next(false);
  }

  enableAuxCheckbox(): void {
    this.AuxEnableCheckbox.next(true);
  }
  disableAuxCheckbox(): void {
    this.AuxEnableCheckbox.next(false);
  }

  enableLsiCheckbox(): void {
    this.LsiEnableCheckbox.next(true);
  }
  disableLsiCheckbox(): void {
    this.LsiEnableCheckbox.next(false);
  }

  enableAocMultiChannelCheckbox(): void {
    this.AocMultiChannelEnableCheckbox.next(true);
  }
  disableAocMultiChannelCheckbox(): void {
    this.AocMultiChannelEnableCheckbox.next(false);
  }

  enableOtpCheckbox(): void {
    this.OtpEnableCheckbox.next(true);
  }
  disableOtpCheckbox(): void {
    this.OtpEnableCheckbox.next(false);
  }

  enableDscCheckbox(): void {
    this.DscEnableCheckbox.next(true);
  }
  disableDscCheckbox(): void {
    this.DscEnableCheckbox.next(false);
  }

  enableLowMainsLockCheckbox(): void {
    this.LowMainsLockEnableCheckbox.next(true);
  }
  disableLowMainsLockCheckbox(): void {
    this.LowMainsLockEnableCheckbox.next(false);
  }

  enableDipswitchCheckbox(): void {
    this.DipswitchEnableCheckbox.next(true);
  }
  disableDipswitchCheckbox(): void {
    this.DipswitchEnableCheckbox.next(false);
  }

  // --- Parent-checkbox notify helpers -------------------------------
  notifyLsiParentCheckbox(checked: boolean): void {
    this.lsiParentCheckbox.next({ checked });
  }
  notifyAocMultiChannelParentCheckbox(checked: boolean): void {
    this.aocMultiChannelParentCheckbox.next({ checked });
  }
  notifyDali102AddressingParentCheckbox(checked: boolean): void {
    this.dali102AddressingParentCheckbox.next({ checked });
  }
  notifyDali102and207ConfigurationParentCheckbox(checked: boolean): void {
    this.dali102and207ConfigurationParentCheckbox.next({ checked });
  }
  notifyOtpParentCheckbox(checked: boolean): void {
    this.otpParentCheckbox.next({ checked });
  }
  notifyDscParentCheckbox(checked: boolean): void {
    this.dscParentCheckbox.next({ checked });
  }
  notifyLowMainsLockParentCheckbox(checked: boolean): void {
    this.lowmainslockParentCheckbox.next({ checked });
  }
  notifyDipswitchParentCheckbox(checked: boolean): void {
    this.dipswitchParentCheckbox.next({ checked });
  }

  // --- One-shot include requests ------------------------------------
  requestDali102AddressingInclude(checked: boolean): void {
    this.dali102AddressingIncludeRequest.next(checked);
  }
}
